import { useCanListMyTimesheets } from '@sit/client-shared';
import { useLogout } from '@web/api/auth/use-logout';
import { useGetUser } from '@web/api/users/use-get-user';
import { useIsAuthenticated } from '@web/auth/use-is-authenticated';
import { useSITNavVisible } from '@web/intacct/nav-menu';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { entriesApprovalsRoute } from '../EntriesApprovals/helpers/routes';
import { leaveApprovalsRoute } from '../LeaveApprovals/helpers/routes';
import { rateCardRoute } from '../RateCards/helpers/routes';
import { timeApprovalsRoute } from '../TimeApprovals/helpers/routes';
import { staffTimeClocksRoute, timeClocksRoute } from '../TimeClocks/helpers/routes';
import { ApplicationNav } from './ApplicationNav';
import { ProfileMenuActions } from './ApplicationNav/ProfileMenu';
import { DashboardFooter } from './DashboardFooter';
import { DebugVersion } from './DebugVersion';
import SecondaryNav, { SecondaryNavTabs } from './SecondaryNav';

const Root = styled.div`
  height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
`;
const NavContainer = styled.div``;
const PageBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;
const Content = styled.div`
  flex: 1;
`;

const EmbeddedModeDebugContainer = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.aboveAll};
`;

const useDashboard = () => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const showApprovalsTab = useGetUser({ enabled: isAuthenticated }).data?.hasApprovals ?? false;
  const navVisible = useSITNavVisible();
  const logout = useLogout();

  const handleApplicationNavAction = useCallback(
    (action: ProfileMenuActions) => {
      switch (action) {
        case 'login':
          return navigate('/login');
        case 'logout':
          return logout();
        case 'settings':
          return navigate('/time-preferences');
      }
    },
    [logout, navigate],
  );

  const handleSecondaryNavTabChange = useCallback(
    (action: SecondaryNavTabs) => {
      switch (action) {
        case 'timesheets':
          return navigate('/timesheets');
        case 'staff-timesheets':
          return navigate('/staff-timesheets');
        case 'approvals':
          return navigate('/approvals');
        case 'data-sources':
          return navigate('/data-sources');
        case 'entries-approvals':
          return navigate(entriesApprovalsRoute);
        case 'time-approvals':
          return navigate(timeApprovalsRoute);
        case 'time-clocks':
          return navigate(timeClocksRoute);
        case 'staff-time-clocks':
          return navigate(staffTimeClocksRoute);
        case 'rate-cards':
          return navigate(rateCardRoute);
        case 'leave':
          return navigate('/leave');
        case 'leave-approvals':
          return navigate(leaveApprovalsRoute);
        case 'staff-leave':
          return navigate('/staff-leave');
        default:
          action satisfies never;
      }
    },
    [navigate],
  );

  return {
    navVisible,
    handleApplicationNavAction,
    handleSecondaryNavTabChange,
    isAuthenticated,
    showApprovalsTab,
  };
};

interface DashboardContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  showSecondaryNav?: boolean;
}

const DashboardContainer = ({ showSecondaryNav, children, ...props }: DashboardContainerProps) => {
  const { navVisible, handleApplicationNavAction, handleSecondaryNavTabChange, isAuthenticated, showApprovalsTab } = useDashboard();
  const canListMyTimesheets = useCanListMyTimesheets();

  return (
    <Root>
      {navVisible && (
        <NavContainer data-testid="navigation-bars">
          <ApplicationNav isAuthenticated={isAuthenticated} onAction={handleApplicationNavAction} />
          {showSecondaryNav && isAuthenticated && (
            <SecondaryNav
              onTabChange={handleSecondaryNavTabChange}
              hideMyTimesheetsTab={!canListMyTimesheets}
              hideApprovalsTab={!showApprovalsTab}
            />
          )}
        </NavContainer>
      )}
      {!navVisible && (
        <EmbeddedModeDebugContainer>
          <DebugVersion />
        </EmbeddedModeDebugContainer>
      )}
      <PageBody>
        <Content {...props}>{children}</Content>
      </PageBody>
      {navVisible && <DashboardFooter />}
    </Root>
  );
};

export default DashboardContainer;
